import {
  CustomerMutationInput,
  getCustomersKey,
  useCheckCustomerSimilarData,
  useCreateCustomer,
} from 'api-hooks/customer';
import { KIND, Notification } from 'baseui/notification';
import { NavigationRoutes } from 'common/constants';
import { PreviousNavigation } from 'common/helpers/previous-navigation';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import useDialog from 'hooks/use-dialog';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { NavigationProps } from 'types';

import CustomerForm from './components/form';

export type CustomerCreateParams = null;

export default function CustomerCreate(
  props: NavigationProps<NavigationRoutes.CustomersCreate>,
) {
  const { navigate, back } = useNavigation();
  const { t } = useTranslation();
  const dialog = useDialog();
  const { mutateAsync, error } = useCreateCustomer();
  const permission = useGetCrudPermission('customers');
  const checkCustomerSimilarMutation = useCheckCustomerSimilarData();
  const onSubmit = React.useCallback(
    async (input: CustomerMutationInput) => {
      const result = await mutateAsync(input);

      queryClient.refetchQueries([getCustomersKey()[0]]);

      const prevPath = PreviousNavigation.get();
      if (prevPath) {
        PreviousNavigation.remove();
        back();
      } else if (permission.view) {
        navigate(NavigationRoutes.CustomersView, {
          id: result.data?.id,
        });
      }

      notification.success({ message: result?.message });

      return result.data;
    },
    [back, mutateAsync, navigate, permission.view],
  );
  return (
    <>
      {!!error && !!error.message && (
        <>
          <Notification
            overrides={{
              Body: { style: { width: 'auto', wordBreak: 'break-all' } },
            }}
            kind={KIND.negative}
            closeable
          >
            {() => error.message}
          </Notification>
          <SeparatorVertical $margin={8} />
        </>
      )}
      <CustomerForm
        {...props}
        onSubmit={async (input, form) => {
          const result = await checkCustomerSimilarMutation.mutateAsync(input);
          const hasSimilarData = result['has_similar_data'];

          if (hasSimilarData) {
            return new Promise((resolve, reject) => {
              dialog.showConfirmation({
                message: t('common:customer_similar_data_description'),
                title: t('common:warning'),
                async onPositiveAction(dismiss) {
                  try {
                    const result = await onSubmit(input);
                    resolve(result);
                    dismiss();
                  } catch (e) {
                    reject(e);
                  }
                },
                onNegativeAction(dismiss) {
                  dismiss();
                },
                onAfterClose() {
                  resolve(undefined);
                },
              });
            });
          } else {
            return await onSubmit(input);
          }
        }}
      />
    </>
  );
}
